import { useEffect, useState, type ReactNode } from 'react';
import { api, ErroApi } from './api';

/**
 * Os filtros em lista suspensa — Situação, Classe e Cliente.
 *
 * ── O pedido ─────────────────────────────────────────────────────────
 *
 * «Filtros em dropdown — Situação e Classe — e filtro por cliente, em
 *  OS / Recebimento / Amostras / Fila / Verificação» — Testesolo,
 * 02/09/2026, repetido em 03/09 («eu já pedi, mas vou pedir novamente»).
 *
 * Até aqui cada lista tinha uma fileira de «chips»: seis botões para a
 * situação, mais outra fileira para a classe. No celular a fileira
 * estourava a largura («Em ex…», UX-026); no desktop ela ocupava duas
 * linhas antes de a tabela começar. E cliente não tinha filtro nenhum —
 * só a busca livre, que acha «Geoazimute» e «Geoazimute Ltda» como se
 * fossem dois.
 *
 * ── Por que `<select>` nativo ────────────────────────────────────────
 *
 * Porque no celular o sistema operacional abre a roleta, com botões do
 * tamanho do dedo, e no desktop a lista cabe numa linha com os outros
 * dois. Um componente próprio faria a mesma coisa, pior, e com mais
 * código para manter.
 *
 * ── Por que o cliente é por ID ───────────────────────────────────────
 *
 * A busca livre compara nome; o filtro compara `cliente_id`. É o que
 * faz «Geoazimute» e «Geoazimute Ltda» serem UM cliente ou DOIS conforme
 * o cadastro diz — e não conforme o texto parece.
 */

export interface OpcaoDeFiltro {
  valor: string;
  texto: string;
  /** Dica ao passar o mouse — a descrição da classe, por exemplo. */
  titulo?: string;
}

/** A faixa que segura os seletores. Quebra linha no celular. */
export function FaixaDeFiltros({ children }: { children: ReactNode }) {
  return <div className="filtros filtros-seletores">{children}</div>;
}

export function Seletor({
  rotulo, valor, opcoes, aoMudar, todos = 'Todas', desabilitado, naBarra,
}: {
  rotulo: string;
  valor: string | null;
  opcoes: OpcaoDeFiltro[];
  aoMudar(v: string | null): void;
  /** O texto da opção vazia — «Todas», «Tudo em aberto», «Qualquer». */
  todos?: string;
  desabilitado?: boolean;
  /**
   * Na barra de ferramentas do padrão Carbon (onda 270): o rótulo pequeno
   * DENTRO do campo, em cima do valor, e o fio inferior — o «dropdown» do
   * Carbon. Fora da barra (formulários, faixas antigas) continua o campo
   * com rótulo acima.
   */
  naBarra?: boolean;
}) {
  return (
    <label className={naBarra ? `barra-filtro${valor ? ' ativo' : ''}` : 'campo filtro-seletor'}>
      <span className="rotulo">{rotulo}</span>
      <select
        value={valor ?? ''}
        disabled={desabilitado}
        onChange={(e) => aoMudar(e.currentTarget.value || null)}
      >
        <option value="">{todos}</option>
        {opcoes.map((o) => (
          <option key={o.valor} value={o.valor} title={o.titulo}>{o.texto}</option>
        ))}
      </select>
    </label>
  );
}

interface ClienteResumo {
  id: string;
  razao_social: string;
  nome_fantasia: string | null;
  status: string;
}

/**
 * A lista de clientes, carregada UMA vez por sessão de tela.
 *
 * Tolera 403: quem tem `ensaio.ver` sem `cadastro.ver` continua vendo a
 * fila; só não filtra por cliente — e o seletor some, em vez de mostrar
 * uma lista vazia sem explicação.
 */
let cacheClientes: ClienteResumo[] | null = null;

export function useClientes() {
  const [lista, setLista] = useState<ClienteResumo[] | null>(cacheClientes);
  const [semPermissao, setSemPermissao] = useState(false);

  useEffect(() => {
    if (cacheClientes) return;
    const abortar = new AbortController();
    api<ClienteResumo[]>('/clientes', { sinal: abortar.signal })
      .then((r) => { cacheClientes = r; setLista(r); })
      .catch((e) => {
        if ((e as Error).name === 'AbortError') return;
        if (e instanceof ErroApi && e.status === 403) setSemPermissao(true);
        setLista([]);
      });
    return () => abortar.abort();
  }, []);

  return { clientes: lista, semPermissao };
}

/** Esquece a lista em memória — para quando um cliente novo é cadastrado. */
export function esquecerClientes() { cacheClientes = null; }

export function SeletorDeCliente({
  valor, aoMudar, naBarra,
}: { valor: string | null; aoMudar(v: string | null): void; naBarra?: boolean }) {
  const { clientes, semPermissao } = useClientes();
  if (semPermissao) return null;
  const opcoes = (clientes ?? [])
    .filter((c) => c.status !== 'inativo' || c.id === valor)
    .map((c) => ({
      valor: c.id,
      texto: c.nome_fantasia && c.nome_fantasia !== c.razao_social
        ? `${c.razao_social} (${c.nome_fantasia})` : c.razao_social,
    }));
  return (
    <Seletor
      rotulo="Cliente"
      valor={valor}
      opcoes={opcoes}
      aoMudar={aoMudar}
      todos="Todos os clientes"
      desabilitado={!clientes}
      naBarra={naBarra}
    />
  );
}

/* ── O seletor de OS ─────────────────────────────────────────────────── */

interface OsResumo { id: string; numero: string; obra_nome: string | null; cliente: string | null }

/**
 * As OS, para filtrar o acervo por uma.
 *
 * Segue o cliente escolhido: com cliente marcado, só as OS dele; sem
 * cliente, todas (as 500 mais recentes, que é o teto da rota). Quem tem
 * `ensaio.ver` sem ver OS continua vendo a lista — o seletor só some.
 */
export function SeletorDeOs({
  valor, cliente, aoMudar, naBarra,
}: {
  valor: string | null; cliente: string | null; aoMudar(v: string | null): void;
  naBarra?: boolean;
}) {
  const [lista, setLista] = useState<OsResumo[] | null>(null);
  const [semPermissao, setSemPermissao] = useState(false);
  useEffect(() => {
    const abortar = new AbortController();
    const q = new URLSearchParams();
    if (cliente) q.set('cliente', cliente);
    setLista(null);
    api<OsResumo[]>(`/os?${q}`, { sinal: abortar.signal })
      .then(setLista)
      .catch((e) => {
        if ((e as Error).name === 'AbortError') return;
        if (e instanceof ErroApi && e.status === 403) setSemPermissao(true);
        setLista([]);
      });
    return () => abortar.abort();
  }, [cliente]);
  if (semPermissao) return null;
  const opcoes = (lista ?? []).map((o) => ({
    valor: o.id,
    texto: [`OS ${o.numero}`, o.obra_nome, cliente ? null : o.cliente]
      .filter(Boolean).join(' · '),
  }));
  return (
    <Seletor
      rotulo={naBarra ? 'OS' : 'Ordem de serviço'}
      valor={valor}
      opcoes={opcoes}
      aoMudar={aoMudar}
      todos="Todas as OS"
      desabilitado={!lista}
      naBarra={naBarra}
    />
  );
}
